// src/features/stock_exchanges/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { FormActionsUpdate } from "@/features/FormCard/Actions";
import { AutocompleteAsyncField, InputField } from "@/features/FormCard/Fields";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { Diff } from "@/features/FormCard/Diff";

// Removed hardcoded countries - using autocomplete instead
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";

const FormSchema = z.object({
  code: z.string().min(1, "Code is required"),
  en_translation: z.string().nullable().optional(),
  fr_translation: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  mic: z.string().nullable().optional(),

  // AutocompleteAsyncField: Display field (country_code)
  country_code: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          text: z.string(), // Country code (e.g., "US", "FR")
        }),
      ])
      .optional(),
  ),

  // AutocompleteAsyncField: ID field (sent via id_column)
  country_id: z.coerce.number().optional().nullable(),
});

export function StockExchangeEditCard({
  code,
  client,
  table_name,
}: {
  code: string;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    handleDelete,
    isDeleting,
  } = useEditForm({
    disable_translations_request: true,
    id: code,
    client,
    table_name,
    FormSchema,
    // Convert country_code from API to autocomplete format for the form
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "country_code") {
        // Get country data from the API response
        const countryId = fetchedData?.country_id;
        const countryCode = fetchedData?.country_code;

        // If we have both country_id and country_code, return autocomplete format
        if (countryId && countryCode) {
          return { id: countryId, text: countryCode };
        }

        // If no country data, return undefined (empty autocomplete)
        return undefined;
      }
      return value;
    },
  });

  return (
    <div className="grid place-items-center h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                StockExchange {code}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Code & Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`${table_name}.columns.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="country_code"
                    label={t(`${table_name}.columns.country_code`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    base_url="instit-quantilia/countries"
                    column_name="code"
                    id_column="country_id"
                    control={form.control}
                  />
                </div>

                {/* Row 2: Translations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="en_translation"
                    label={t(`${table_name}.columns.en_translation`, {
                      ns: "tables",
                      defaultValue: "English Translation",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="fr_translation"
                    label={t(`${table_name}.columns.fr_translation`, {
                      ns: "tables",
                      defaultValue: "French Translation",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Row 3: City & MIC */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="city"
                    label={t(`${table_name}.columns.city`, {
                      ns: "tables",
                      defaultValue: "City",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="mic"
                    label={t(`${table_name}.columns.mic`, {
                      ns: "tables",
                      defaultValue: "MIC",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={form.formState.isDirty}
                  elementId={code}
                  client={client}
                  allowDelete={false}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? Diff(changedFields, ["country_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </Card>
      </div>
    </div>
  );
}
